import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from "react";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Stack,
  Typography,
   Snackbar,
} from "@mui/material";
//import { createRoot } from 'react-dom/client';
import {
  DEFAULT_ROLE,
  getAllInsightConfigs,
  getAllKpiConfigs,
  getRolePermissions,
  initBaseDashboard,
  ROLE_PERMISSIONS,
} from "./globalData.js";
import AgingCard from "./AgingCard.jsx";
import CategoryMixCard from "./CategoryMixCard.jsx";
import CategorySpendCard from "./CategorySpendCard.jsx";
import FulfilmentFunnelCard from "./FulfilmentFunnelCard.jsx";
import InsightCard from "./InsightCard.jsx";
import InventoryActionCenterCard from "./InventoryActionCenterCard.jsx";
import InventoryCategoryCard from "./InventoryCategoryCard.jsx";
import InvoiceExceptionsCard from "./InvoiceExceptionsCard.jsx";
import InvoiceStatusCard from "./InvoiceStatusCard.jsx";
import InvoiceTableCard from "./InvoiceTableCard.jsx";
import KpiCards from "./KpiCards.jsx";
import ManagementActionCenterCard from "./ManagementActionCenterCard.jsx";
import ManagementKpiDrilldown from "./ManagementKpiDrilldown.jsx";
import MonthlySpendCard from "./MonthlySpendCard.jsx";
import OperationsDetailDrilldown from "./OperationsDetailDrilldown.jsx";
import OutstandingPayablesCard from "./OutstandingPayablesCard.jsx";
import POStatusCard from "./POStatusCard.jsx";
import PaymentCenterCard from "./PaymentCenterCard.jsx";
import PaymentStatusCard from "./PaymentStatusCard.jsx";
import PerformanceTrendCard from "./PerformanceTrendCard.jsx";
import ProcurementSavingsCard from "./ProcurementSavingsCard.jsx";
import PurchaseOrdersCard from "./PurchaseOrdersCard.jsx";
import ReorderTableCard from "./ReorderTableCard.jsx";
import SlowStockCard from "./SlowStockCard.jsx";
import StockMovementCard from "./StockMovementCard.jsx";
import SupplierConcentrationCard from "./SupplierConcentrationCard.jsx";
import TopVendorsCard from "./TopVendorsCard.jsx";
import UpcomingDeliveriesCard from "./UpcomingDeliveriesCard.jsx";
import UpcomingPaymentsCard from "./UpcomingPaymentsCard.jsx";
import VendorActionCenterCard from "./VendorActionCenterCard.jsx";
import VendorDelaysCard from "./VendorDelaysCard.jsx";
import VendorDetailDrilldown from "./VendorDetailDrilldown.jsx";
import VendorGrowthCard from "./VendorGrowthCard.jsx";
import Supplier360 from "./Supplier360.jsx";
import "./global.css";
import Service from "../../Services/Service";

// Role and dashboard data hooks
const RoleContext = createContext(null);
const DASHBOARD_API_URL = process.env.REACT_APP_DASHBOARD_API_URL || "";

export function useDashboardData(endpoint, options = {}) {
  const { params = {}, initialData, mockData, onSuccess, onError } = options;
  const role = useCurrentRole();
  const [data, setData] = useState(initialData || mockData || null);
  const [loading, setLoading] = useState(!initialData && !mockData);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (mockData) {
      setData(mockData);
      setLoading(false);
      onSuccess?.(mockData);
      return;
    }
    if (!endpoint) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const queryString = new URLSearchParams({ role, ...params }).toString();
      const response = await fetch(`${endpoint}?${queryString}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
      onSuccess?.(result);
    } catch (fetchError) {
      setError(fetchError.message);
      onError?.(fetchError);
    } finally {
      setLoading(false);
    }
  }, [endpoint, role, params, mockData, onSuccess, onError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function RoleProvider({ children, initialRole = DEFAULT_ROLE }) {
  const [userRole, setUserRole] = useState(initialRole);
  const [dashboardData, setDashboardData] = useState(null);
  const [apiState, setApiState] = useState({
    loading: Boolean(DASHBOARD_API_URL),
    error: null,
  });

  useEffect(() => {
    if (!DASHBOARD_API_URL) {
      setDashboardData(null);
      setApiState({ loading: false, error: null });
      return undefined;
    }

    const controller = new AbortController();
    const loadDashboard = async () => {
      setApiState({ loading: true, error: null });
      setDashboardData(null);
      try {
        const url = new URL(DASHBOARD_API_URL, window.location.origin);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Dashboard API returned HTTP ${response.status}`);
        }

        const result = await response.json();
        if (
          typeof result?.role !== "string" ||
          !result.permissions ||
          !Array.isArray(result.permissions.kpis) ||
          !Array.isArray(result.permissions.insights) ||
          !Array.isArray(result.permissions.canAccess)
        ) {
          throw new Error(
            "Dashboard API response must include the assigned role and its permissions.",
          );
        }
        setDashboardData(result);
        setUserRole(result.role);
      } catch (error) {
        if (error.name !== "AbortError") {
          setApiState({ loading: false, error: error.message });
          return;
        }
      } finally {
        if (!controller.signal.aborted) {
          setApiState((current) => ({ ...current, loading: false }));
        }
      }
    };

    loadDashboard();
    return () => controller.abort();
  }, []);

  const permissions = useMemo(
    () =>
      dashboardData?.permissions ||
      (DASHBOARD_API_URL
        ? { kpis: [], insights: [], canAccess: [] }
        : getRolePermissions(userRole)),
    [dashboardData, userRole],
  );
  const kpiConfigs = useMemo(() => {
    const availableConfigs = getAllKpiConfigs();
    const configs = availableConfigs.filter(({ id }) =>
      permissions.kpis.includes(id),
    );
    return configs.map((config) => ({
      ...config,
      ...(dashboardData?.kpis?.[config.id] || {}),
      ...(DASHBOARD_API_URL && !dashboardData?.kpis?.[config.id]
        ? { value: "—", trend: { value: "", type: "flat" } }
        : {}),
      ...(DASHBOARD_API_URL ? { onClick: null } : {}),
      id: config.id,
    }));
  }, [permissions.kpis, dashboardData]);
  const insightConfigs = useMemo(() => {
    const availableConfigs = getAllInsightConfigs();
    const configs = DASHBOARD_API_URL
      ? availableConfigs.filter(({ id }) => permissions.insights.includes(id))
      : availableConfigs;
    return configs.map((config) => ({
      ...config,
      ...(dashboardData?.insights?.[config.id] || {}),
      id: config.id,
    }));
  }, [permissions.insights, dashboardData]);

  const switchRole = useCallback((role) => {
    if (!DASHBOARD_API_URL && ROLE_PERMISSIONS[role]) {
      setUserRole(role);
    }
  }, []);
  const hasPermission = useCallback(
    (permission) =>
      permissions.kpis.includes(permission) ||
      permissions.insights.includes(permission),
    [permissions],
  );
  const canAccess = useCallback(
    (module) => permissions.canAccess?.includes(module) || false,
    [permissions],
  );
  const value = useMemo(
    () => ({
      userRole,
      permissions,
      kpiConfigs,
      insightConfigs,
      dashboardData,
      apiLoading: apiState.loading,
      apiError: apiState.error,
      apiEnabled: Boolean(DASHBOARD_API_URL),
      switchRole,
      hasPermission,
      canAccess,
    }),
    [
      userRole,
      permissions,
      kpiConfigs,
      insightConfigs,
      dashboardData,
      apiState,
      switchRole,
      hasPermission,
      canAccess,
    ],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRoleAccess() {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRoleAccess must be used within a RoleProvider");
  }
  return context;
}

export function useKpiConfigs() {
  return useRoleAccess().kpiConfigs;
}

export function useInsightConfigs() {
  return useRoleAccess().insightConfigs;
}

export function usePermissions() {
  return useRoleAccess().permissions;
}

export function useCurrentRole() {
  return useRoleAccess().userRole;
}

export function useCanAccess(module) {
  return useRoleAccess().canAccess(module);
}

// Optional API data provider for components using render-prop data access.
export function DataProvider({
  children,
  endpoint,
  role,
  params = {},
  initialData,
  onDataLoaded,
  onError,
  mockData,
}) {
  const [data, setData] = useState(initialData || mockData || null);
  const [loading, setLoading] = useState(!initialData && !mockData);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (mockData) {
      setData(mockData);
      setLoading(false);
      onDataLoaded?.(mockData);
      return;
    }
    if (!endpoint) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const queryString = new URLSearchParams({ role, ...params }).toString();
      const response = await fetch(`${endpoint}?${queryString}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
      onDataLoaded?.(result);
    } catch (fetchError) {
      setError(fetchError.message);
      onError?.(fetchError);
    } finally {
      setLoading(false);
    }
  }, [endpoint, role, params, mockData, onDataLoaded, onError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" variant="filled" onClose={() => setError(null)}>
        <AlertTitle>Failed to load data</AlertTitle>
        {error}
      </Alert>
    );
  }
  return children({ data, loading, error, refetch: fetchData });
}

export function createDataProvider(endpoint, options = {}) {
  return function DataProviderWrapper({ children, role, ...props }) {
    return (
      <DataProvider endpoint={endpoint} role={role} {...options} {...props}>
        {children}
      </DataProvider>
    );
  };
}

// Optional UI access gate. Backend APIs must enforce their own permissions.
export function RoleGate({
  children,
  allowedRoles,
  requiredPermission,
  fallback,
  userRole,
}) {
  const permissions = getRolePermissions(userRole);
  const hasRoleAccess =
    !allowedRoles ||
    allowedRoles.includes(userRole) ||
    allowedRoles.some((role) => permissions.canAccess?.includes(role));
  const hasPermission =
    !requiredPermission ||
    permissions.kpis.includes(requiredPermission) ||
    permissions.insights.includes(requiredPermission);

  if (!hasRoleAccess || !hasPermission) {
    return (
      fallback || (
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Alert severity="info" variant="filled">
            <AlertTitle>Access Restricted</AlertTitle>
            You don&apos;t have permission to view this content. Required role:{" "}
            {allowedRoles?.join(", ") || requiredPermission}
          </Alert>
        </Box>
      )
    );
  }
  return children;
}

export function withRoleGate(Component, { allowedRoles, requiredPermission }) {
  return function WithRoleGateWrapper({ userRole, ...props }) {
    return (
      <RoleGate
        allowedRoles={allowedRoles}
        requiredPermission={requiredPermission}
        userRole={userRole}
      >
        <Component {...props} />
      </RoleGate>
    );
  };
}

function DashboardContent() {
  const { apiEnabled, apiLoading, apiError } = useRoleAccess();
  const [openSnackbar, setOpenSnackbar] = useState(false);
const [loginUsername, setLoginUsername] = useState("");

useEffect(() => {
  if (sessionStorage.getItem("showLoginSuccess") === "true") {
    const user =
      sessionStorage.getItem("loginSuccessUser") ||
      sessionStorage.getItem("empname") ||
      sessionStorage.getItem("userEmail") ||
      "User";

    setLoginUsername(user);
    setOpenSnackbar(true);

    sessionStorage.removeItem("showLoginSuccess");
    sessionStorage.removeItem("loginSuccessUser");
  }
}, []);

  return (
    <Box className="app-shell-root">
      <Box className="content">
        {apiLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : apiError ? (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>Unable to load role dashboard</AlertTitle>
            {apiError}
          </Alert>
        ) : (
          <DashboardView />
        )}
      </Box>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={3000}
  onClose={(event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  }}
  anchorOrigin={{ vertical: "top", horizontal: "right" }}
  sx={{
    top: "24px !important",
    right: "24px !important",
    zIndex: 99999,
  }}
>
  <div className="custom-toast">
    <div className="custom-toast-icon-wrapper">
      <div className="custom-toast-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      </div>
    </div>

    <div className="custom-toast-divider"></div>

    <div className="custom-toast-content">
      <strong>Login successful!</strong> Welcome{" "}
      <span className="toast-email">{loginUsername}</span>
    </div>

    <div className="custom-toast-progress"></div>
  </div>
</Snackbar>
    </Box>
  );
}

export default function App() {
  return (
    <RoleProvider initialRole="finance_user">
      <DashboardContent />
    </RoleProvider>
  );
}

function ApiInsightData({ data }) {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const hasContent =
    data?.value != null ||
    data?.summary ||
    data?.trend?.value ||
    rows.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      {data?.value != null && (
        <Typography variant="h5" fontWeight={700}>
          {data.value}
        </Typography>
      )}
      {data?.summary && (
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          {data.summary}
        </Typography>
      )}
      {data?.trend?.value && (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
          {data.trend.value}
        </Typography>
      )}
      {rows.length > 0 && (
        <Box component="ul" sx={{ mb: 0, pl: 2 }}>
          {rows.map((row, index) => (
            <li key={row?.id || index}>
              {typeof row === "string"
                ? row
                : row
                  ? `${row.label}: ${row.value}`
                  : ""}
            </li>
          ))}
        </Box>
      )}
      {!hasContent && (
        <Typography color="text.secondary">No data available.</Typography>
      )}
    </Box>
  );
}

function DashboardView() {
  const { kpiConfigs, insightConfigs, dashboardData, apiEnabled } =
    useRoleAccess();

  // Database-driven dashboard permissions
  const [dashboardDivs, setDashboardDivs] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState("");

  useEffect(() => {
    if (!accessLoading && !accessError && !apiEnabled) {
      initBaseDashboard();
    }
  }, [accessLoading, accessError, apiEnabled]);

  //   useEffect(() => {
  //     let cancelled = false;

  //     const loadDashboardAccess = async () => {

  //       try {

  //               console.log("GroupId:", sessionStorage.getItem("GroupId"));
  // console.log("EmpNo:", sessionStorage.getItem("EmpNo"));
  // console.log("ALL_MENUS:", sessionStorage.getItem("ALL_MENUS"));
  // console.log("APIUrl:", sessionStorage.getItem("APIUrl"));
  //         const groupID = sessionStorage.getItem('GroupId');
  //         const empNo = sessionStorage.getItem('EmpNo');

  //         const allMenus = JSON.parse(
  //           sessionStorage.getItem('ALL_MENUS') || '[]'
  //         );

  //         console.table(allMenus);

  //         // New dashboard MenuID = 753
  //         const dashboardMenu = allMenus.find(
  //           (menu) =>
  //             Number(menu.mnuId ?? menu.MnuId) === 753
  //         );

  //         const menuID =
  //           dashboardMenu?.mnuId ?? dashboardMenu?.MnuId;

  //         console.log('Dashboard GroupID:', groupID);
  //         console.log('Dashboard EmpNo:', empNo);
  //         console.log('Dashboard MenuID:', menuID);

  //         if (!groupID || !empNo || !menuID) {
  //           throw new Error(
  //             'GroupId, EmpNo, or dashboard MenuID is missing.'
  //           );
  //         }

  //         Service.setApiUrl(
  //           `${sessionStorage.getItem('APIUrl')}/api/DashBoard`
  //         );

  //         const res =
  //           await Service.getDashboardDivAccessByEmpNo(
  //             groupID,
  //             empNo,
  //             menuID
  //           );

  //         console.log(
  //           'Employee Dashboard Div Access:',
  //           res.data
  //         );

  //         if (!cancelled) {
  //           setDashboardDivs(
  //             Array.isArray(res.data) ? res.data : []
  //           );
  //           setAccessError('');
  //         }
  //       } catch (error) {
  //         console.error(
  //           'Dashboard access API error:',
  //           error
  //         );

  //         if (!cancelled) {
  //           setDashboardDivs([]);
  //           setAccessError(
  //             'Unable to load dashboard permissions.'
  //           );
  //         }
  //       } finally {
  //         if (!cancelled) {
  //           setAccessLoading(false);
  //         }
  //       }
  //     };

  //     loadDashboardAccess();

  //     return () => {
  //       cancelled = true;
  //     };
  //   }, []);

  // Same access-checking logic as VendorView

  useEffect(() => {
    let cancelled = false;
    let timerId;

    const loadDashboardAccess = async () => {
      const groupID = sessionStorage.getItem("GroupId");
      const empNo = sessionStorage.getItem("EmpNo");
      const apiUrl = sessionStorage.getItem("APIUrl");

      let allMenus = [];

      try {
        allMenus = JSON.parse(sessionStorage.getItem("ALL_MENUS") || "[]");
      } catch (error) {
        console.error("Invalid ALL_MENUS data:", error);
      }

      const dashboardMenu = allMenus.find(
        //local
        //(menu) => Number(menu.mnuId ?? menu.MnuId) === 753,
        //live
        (menu) => Number(menu.mnuId ?? menu.MnuId) === 743
      );

      const menuID = dashboardMenu?.mnuId ?? dashboardMenu?.MnuId;

      // Wait for the sidebar menu data to become available.
      if (!groupID || !empNo || !apiUrl || !menuID) {
        if (!cancelled) {
          timerId = setTimeout(loadDashboardAccess, 300);
        }
        return;
      }

      try {
        Service.setApiUrl(`${apiUrl}/api/DashBoard`);
        //debugger;
        const res = await Service.getDashboardDivAccessByEmpNo(
          groupID,
          empNo,
          menuID,
        );

        if (!cancelled) {
          setDashboardDivs(Array.isArray(res.data) ? res.data : []);
          setAccessError("");
          setAccessLoading(false);
        }
      } catch (error) {
        console.error("Dashboard access API error:", error);

        if (!cancelled) {
          setDashboardDivs([]);
          setAccessError("Unable to load dashboard permissions.");
          setAccessLoading(false);
        }
      }
    };

    loadDashboardAccess();

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, []);

  const hasAccess = (divKey) =>
    dashboardDivs.some(
      (d) =>
        d.divKey === divKey &&
        (d.accessValue === true ||
          d.accessValue === 1 ||
          d.accessValue === "1"),
    );

  const insightById = new Map(
    insightConfigs.map((insight) => [insight.id, insight]),
  );
  const hasInsight = (id) => insightById.has(id);

  // const renderInsight = (id, component) => {
  //   const insight = insightById.get(id);
  //   if (!insight) return null;

  //   return (
  //     <Box
  //       key={id}
  //       className={`dashboard-insight${insight.span === 2 ? ' dashboard-insight--wide' : ''}`}
  //       data-insight={id}
  //     >
  //       {apiEnabled ? (
  //         <InsightCard title={insight.title} span={insight.span} id={`insight-${id}`}>
  //           <ApiInsightData data={dashboardData?.insights?.[id]} />
  //         </InsightCard>
  //       ) : component}
  //     </Box>
  //   );
  // };

  const insightDivKeys = {
    monthlySpend: "MonthlySpendCard",
    categorySpend: "CategorySpendCard",
    paymentStatus: "PaymentStatusCard",
    topVendors: "TopVendorsCard",
    outstandingPayables: "OutstandingPayablesCard",
    aging: "AgingCard",
    upcomingPayments: "UpcomingPaymentsCard",
    invoiceExceptions: "InvoiceExceptionsCard",
    invoiceTable: "InvoiceTableCard",
    stockMovement: "StockMovementCard",
    fulfilmentFunnel: "FulfillmentPanelCard",
    inventoryCategory: "InventoryCategoryCard",
    vendorDelays: "VendorDelaysCard",
    slowStock: "SlowStockCard",
    inventoryActionCenter: "InventoryActionCenterCard",
    reorderTable: "ReorderTableCard",
    vendorGrowth: "VendorGrowthCard",
    procurementSavings: "ProcurementSavingsCard",
    categoryMix: "CategoryMixCard",
    managementActionCenter: "ManagementActionCenterCard",
    supplierConcentration: "SupplierConcentrationCard",
    poStatus: "POStatusCard",
    performanceTrend: "PerformanceTrendCard",
    invoiceStatus: "InvoiceStatusCard",
    paymentCenter: "PaymentCenterCard",
    vendorActionCenter: "VendorActionCenterCard",
    upcomingDeliveries: "UpcomingDeliveriesCard",
    purchaseOrders: "PurchaseOrdersCard",
  };

  const renderInsight = (id, component) => {
    const insight = insightById.get(id);
    const divKey = insightDivKeys[id];

    if (!insight || !divKey || !hasAccess(divKey)) {
      return null;
    }

    return (
      <Box
        key={id}
        className={`dashboard-insight${
          insight.span === 2 ? " dashboard-insight--wide" : ""
        }`}
        data-insight={id}
      >
        {apiEnabled ? (
          <InsightCard
            title={insight.title}
            span={insight.span}
            id={`insight-${id}`}
          >
            <ApiInsightData data={dashboardData?.insights?.[id]} />
          </InsightCard>
        ) : (
          component
        )}
      </Box>
    );
  };

  if (accessLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (accessError) {
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        {accessError}
      </Alert>
    );
  }

  return (
    <Box className="page active" id="page-dashboard">
      <Typography className="dashboard-role-label" component="h1" variant="h5">
        Insights Dashboard
      </Typography>
      {hasAccess("KpiCards") && <KpiCards configs={kpiConfigs} />}
      <Box
        className="dashboard-insights-grid"
        role="region"
        aria-label="Insights"
      >
        {renderInsight("monthlySpend", <MonthlySpendCard />)}
        {renderInsight("categorySpend", <CategorySpendCard />)}
        {renderInsight("paymentStatus", <PaymentStatusCard />)}
        {renderInsight("topVendors", <TopVendorsCard />)}
        {renderInsight("outstandingPayables", <OutstandingPayablesCard />)}
        {renderInsight("aging", <AgingCard />)}
        {renderInsight("upcomingPayments", <UpcomingPaymentsCard />)}
        {renderInsight("invoiceExceptions", <InvoiceExceptionsCard />)}
        {renderInsight("invoiceTable", <InvoiceTableCard />)}
        {renderInsight("stockMovement", <StockMovementCard />)}
        {renderInsight("fulfilmentFunnel", <FulfilmentFunnelCard />)}
        {renderInsight("inventoryCategory", <InventoryCategoryCard />)}
        {renderInsight("vendorDelays", <VendorDelaysCard />)}
        {renderInsight("slowStock", <SlowStockCard />)}
        {renderInsight("inventoryActionCenter", <InventoryActionCenterCard />)}
        {renderInsight("reorderTable", <ReorderTableCard />)}
        {renderInsight("vendorGrowth", <VendorGrowthCard />)}
        {renderInsight("procurementSavings", <ProcurementSavingsCard />)}
        {renderInsight("categoryMix", <CategoryMixCard />)}
        {renderInsight(
          "managementActionCenter",
          <ManagementActionCenterCard />,
        )}
        {renderInsight("supplierConcentration", <SupplierConcentrationCard />)}
        {renderInsight("poStatus", <POStatusCard />)}
        {renderInsight("performanceTrend", <PerformanceTrendCard />)}
        {renderInsight("invoiceStatus", <InvoiceStatusCard />)}
        {renderInsight("paymentCenter", <PaymentCenterCard />)}
        {renderInsight("vendorActionCenter", <VendorActionCenterCard />)}
        {renderInsight("upcomingDeliveries", <UpcomingDeliveriesCard />)}
        {renderInsight("purchaseOrders", <PurchaseOrdersCard />)}
      </Box>

      {hasAccess("ManagementKpiDrilldown") && hasInsight("vendorGrowth") && (
        <ManagementKpiDrilldown />
      )}

      {hasAccess("VendorDetailDrilldown") && hasInsight("poStatus") && (
        <VendorDetailDrilldown />
      )}

      {hasAccess("OperationsDetailDrilldown") &&
        hasInsight("stockMovement") && <OperationsDetailDrilldown />}

      {kpiConfigs.length === 0 && insightConfigs.length === 0 && (
        <Alert severity="info">
          No dashboard insights are assigned to this role.
        </Alert>
      )}
      {hasAccess("Supplier360") && <Supplier360 />}
    </Box>
  );
}

//createRoot(document.getElementById('root')).render(<App />);
